import { Component, OnInit, Input } from '@angular/core';
import { CurrencyPipe } from "@angular/common";
import { Observable, of } from "rxjs";
import { PaymentService } from "../../../payments/services/payment.service";
import { PaymentRecordAdjustment, PaymentStatus, PaymentTokenDisplay, PaymentType, ApplicationPayment, ProofOfPurchaseTypes } from "../../../payments/models/paymentItem.model";
import { GenerateReportModel } from "../../../reports/model/reports.model";


@Component({
    selector: 'reapplication-receipt',
    templateUrl: './reapplication-receipt.component.html',
    styleUrls: ['./reapplication-receipt.component.scss'],
    standalone: false
})
export class ReapplicationReceiptComponent implements OnInit {

    _sources: PaymentRecordAdjustment[] = [];

    get adjustmentSources(): PaymentRecordAdjustment[] {
        return this._sources;
    }

    @Input() set adjustmentSources(value: PaymentRecordAdjustment[]) {
        this._sources = value;
    }

    @Input() public applicationNumber: string;
    @Input() isPaid: boolean = false;

    @Input() records: ApplicationPayment[];

    @Input() requestedDate: Date;


    get filler(): string {
        return "............................................................................................................................................";
    }



    get organizationId(): string {
        if (this.records == null || this.records.length == 0) return "";
        return this.records[0].organizationId;
    }

    get organizationName(): string {
        if (this.records == null || this.records.length == 0) return "";
        return this.records[0].organization;
    }

    get paymentTypes(): string[] {
        return this.getPaymentTypes();
    }

    get remainingDue(): string {
        const amt = this.getTotalDue() - this.getTotalPaid();
        return this.pipe.transform(amt, "USD", "symbol", "1.2-2");
    }

    get totalPaid(): string {
        return this.pipe.transform(this.getTotalPaid(), "USD", "symbol", "1.2-2");
    }

    get totalDue(): string {
        return this.pipe.transform(this.getTotalDue(), "USD", "symbol", "1.2-2");
    }

    constructor(private readonly pipe: CurrencyPipe, private paymentService: PaymentService) {

    }

    ngOnInit() {
    }

    generatePdf(): Observable<string> {
        const documentType = this.isPaid ? ProofOfPurchaseTypes.Receipt : ProofOfPurchaseTypes.Invoice;
        const content = this.getContent(documentType);
        if (content && content.innerHTML) {
            const model = new GenerateReportModel();
            model.html = content.innerHTML.replace('#dddddd', 'transparent');
            model.organizationId = this.organizationId;
            model.organizationName = this.organizationName;
            model.applicationNumber = this.applicationNumber;
            model.useBanner = true;
            model.formatAsLetter = true;
            model.usePageNumberHeader = false;
            model.styleMergeFields = false;
            model.documentTypeName = (this.isPaid) ? this.records[0].receiptDocumentType : this.records[0].invoiceDocumentType;
            model.flowId = this.records[0].workflowId;
            return this.paymentService.getPdf(model, documentType);
        }

        return of(null);
    }

    getContent(documentType: ProofOfPurchaseTypes): HTMLElement {
        const contentSrc = this.getDocumentId(documentType);
        const content = document.getElementById(contentSrc);
        return content;
    }

    getDisplayTokens(tokens: string[]): PaymentTokenDisplay[] {
        const display: PaymentTokenDisplay[] = [];
        tokens.forEach(t => display.push(new PaymentTokenDisplay(t)));          
        return display;
    }

    getDocumentId(docType: string): string {
        return `${this.applicationNumber}_${docType}`;
    }

    getPaymentTypes(): string[] {
        const list: string[] = [];
        if (this.records) {
            for (let r = 0; r < this.records.length; r++) {
                if (this.records[r].amountPaid !== 0 && this.records[r].paymentRecords != null) {
                    if (this.records[r].amountDue == 0 && this.records[r].paymentStatus != PaymentStatus.Paid) this.records[r].paymentStatus = PaymentStatus.Paid;
                    for (let p = 0; p < this.records[r].paymentRecords.length; p++) {
                        let token = this.records[r].paymentRecords[p].token;
                        if (token == null) {
                            if (this.records[r].paymentRecords[p].paymentType != null) {
                                let paymentType = this.records[r].paymentRecords[p].paymentType;
                                switch (paymentType) {
                                    case PaymentType.PaidCash:
                                        token = `Cash in the amount of ${this.pipe.transform(this.records[r].paymentRecords[p].amount)}`;
                                        break;
                                    case PaymentType.LocalCheck:
                                    case PaymentType.ACH:
                                        token = `Check #${this.records[r].paymentRecords[p].tokenId} in the amount of ${this.pipe.transform(this.records[r].paymentRecords[p].amount)}`;
                                        break;
                                    case PaymentType.MoneyOrder:
                                        token = `Money Order #${this.records[r].paymentRecords[p].tokenId} in the amount of ${this.pipe.transform(this.records[r].paymentRecords[p].amount)}`;
                                        break;
                                    case PaymentType.Credit:
                                        token = `Credit Authorization #${this.records[r].paymentRecords[p].tokenId} in the amount of ${this.pipe.transform(this.records[r].paymentRecords[p].amount)}`;
                                        break;
                                }
                            } else { token = "" } //default case to keep component from blowing up on a null reference

                        }
                        list.push(token);
                    }
                }
            }
        }

        return list;
    }

    getTotalDue(): number {
        let value = 0;
        this.records.forEach(r => {
            const amt = r.fee;
            value = value + amt;
        });
        return value;
    }

    getTotalPaid(): number {
        let value = 0;
        for (let r = 0; r < this.records.length; r++) {
            value = value + this.records[r].amountPaid;
        }
        return value;
    }
}
